import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {ListResult, SingleResult} from 'utils/types';
import {addResultCases, stripUndefined} from 'utils/helpers';
import ApiService from 'redux/services/api-service';

export interface CampaignBenchmarkData {
  id: number;
  campaign_id: number | null;
  campaign_analysis_run_id: number | null;
  main_campaign_benchmark_data_id: number | null;
  monday_item_id: number | null;
  name: string;
  result_type: string;
  client_id: number | null;
  client_name: string;
  retailer_id: number | null;
  retailer_name: string;
  category_id: number | null;
  lift_percent: string | null;
  hero_lift_percent: string | null;
  halo_lift_percent: string | null;
  incremental_sales: string | null;
  hero_incremental_sales: string | null;
  halo_incremental_sales: string | null;
  total_campaign_sales: string | null;
  roas: string | null;
  method_needed_to_transf: string[];
  method: string[];
  year: number | null;
  main_path_id: string;
  associated_path_id: string;
  chicory_io: string;
  po_dollars: string | null;
  brand: string;
  service: string[];
  budget: number | null;
  impressions: number | null;
  project_confirmed_date: string | null;
  request_date_rfp: string | null;
  data_source: string;
  email: string;
  campaign_start_date: string | null;
  campaign_end_date: string | null;
  topline_result_id: number | null;
  retailer_result_id: string;
  created_at: string;
}

export interface CampaignBenchmarkDataOptions {
  brand: string[];
  category_name: string[];
  client_name: string[];
  retailer_name: string[];
}

export interface MainPathIDOptions {
  main_path_ids: string[];
}

export interface AssociatedPathIDOptions {
  associated_path_ids: string[];
}

export type CampaignBenchmarkDataParams = {
  campaign_id?: number | string;
  client_id?: number | string;
  retailer_id?: number | string;
  result_type?: string;
  topline_result_id?: number | string;
  retailer_result_id?: string;
  data_source?: string;
  year?: number | string;
  search?: string;
  ordering?: string;
  limit?: number;
  offset?: number;
};

export const campaignBenchmarkDataEndpoints = {
  list: createAsyncThunk('campaignBenchmarkData/list', async (params: CampaignBenchmarkDataParams) => {
    const queryParams = new URLSearchParams(stripUndefined(params as Record<string, any>));
    const response = await ApiService.get(`/campaign_benchmark_data/?${queryParams.toString()}`);
    return response.data;
  }),
  exists: createAsyncThunk(
    'campaignBenchmarkData/exists',
    async (params: Pick<CampaignBenchmarkDataParams, 'campaign_id'>) => {
      const queryParams = new URLSearchParams(stripUndefined(params as Record<string, any>));
      const response = await ApiService.get(`/campaign_benchmark_data/exists/?${queryParams.toString()}`);
      return response.data as {exists: boolean};
    },
  ),
  getFilterOptions: createAsyncThunk('campaignBenchmarkData/filterOptions', async () => {
    const response = await ApiService.get('/campaign_benchmark_data/filter_options/');
    return response.data;
  }),
  getMainPathIDOptions: createAsyncThunk('campaignBenchmarkData/mainPathIDOptions', async () => {
    const response = await ApiService.get('/campaign_benchmark_data/main_path_id_options/');
    return response.data;
  }),
  getAssociatedPathIDOptions: createAsyncThunk('campaignBenchmarkData/associatedPathIDOptions', async () => {
    const response = await ApiService.get('/campaign_benchmark_data/associated_path_id_options/');
    return response.data;
  }),
  getUpdates: createAsyncThunk('campaignBenchmarkData/updates', async (campaign_benchmark_data_id: number) => {
    const response = await ApiService.get(`/campaign_benchmark_data/${campaign_benchmark_data_id}/updates/`);
    return response.data;
  }),
  download: createAsyncThunk(
    'campaignBenchmarkData/download',
    async (
      params: CampaignBenchmarkDataParams & {
        file_type: 'csv' | 'xlsx';
        include_updates?: 'true' | 'false';
      },
    ) => {
      const queryParams = new URLSearchParams(stripUndefined(params as Record<string, any>));
      const response = await ApiService.get(`/campaign_benchmark_data/download/?${queryParams.toString()}`, {
        responseType: 'blob',
      });
      return response;
    },
  ),
};

interface IState {
  results: ListResult<CampaignBenchmarkData>;
  options: SingleResult<CampaignBenchmarkDataOptions>;
  mainPathIDOptions: SingleResult<MainPathIDOptions>;
  associatedPathIDOptions: SingleResult<AssociatedPathIDOptions>;
}

export const campaignBenchmarkDataSlice = createSlice({
  name: 'campaignBenchmarkData',
  initialState: {
    results: {as: 'idle'},
    options: {as: 'idle'},
    mainPathIDOptions: {as: 'idle'},
    associatedPathIDOptions: {as: 'idle'},
  } as IState,
  reducers: {},
  extraReducers: builder => {
    addResultCases(builder, campaignBenchmarkDataEndpoints.list, 'results');
    addResultCases(builder, campaignBenchmarkDataEndpoints.getFilterOptions, 'options');
    addResultCases(builder, campaignBenchmarkDataEndpoints.getMainPathIDOptions, 'mainPathIDOptions');
    addResultCases(builder, campaignBenchmarkDataEndpoints.getAssociatedPathIDOptions, 'associatedPathIDOptions');
  },
});

export type CampaignBenchmarkDataSlice = {
  [campaignBenchmarkDataSlice.name]: ReturnType<(typeof campaignBenchmarkDataSlice)['reducer']>;
};
